import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { injectQuery, QueryObserverResult } from '@ngneat/query';
import {
  ApiResponse,
  DockGroupsResponse,
  Site,
  SiteCapabilities,
  SiteFreightTypes,
  SiteLocation,
  SiteScacCodes,
  SiteTrailerStatus,
  SiteTrailerTypes,
} from 'app/shared/types';
import { minutesToMilliseconds } from 'date-fns';
import { environment } from 'environment/environment';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CACHE_KEYS } from '../enums/cache-keys';
import { Result } from '../types/cache';
import { BaseApiService } from './base-api.service';
import { CONTROLLERS } from './controllers';

@Injectable({
  providedIn: 'root',
})
export class SitesService extends BaseApiService<
  ApiResponse<{ sites: Site[] }>,
  ApiResponse<{ site: Site }>,
  ApiResponse<{ id: string }>
> {
  private query = injectQuery();

  constructor(public override http: HttpClient) {
    super(http, environment.siteManagementApiBaseUrl);
  }

  public override controllerName(): string {
    return CONTROLLERS.sites;
  }

  public getSiteLocations(
    siteId: string,
    isLocationsHierarchyEnabled?: boolean,
  ): Observable<
    ApiResponse<{
      locations: SiteLocation[];
    }>
  > {
    let params = new HttpParams();
    if (isLocationsHierarchyEnabled) {
      params = params.set('api-version', '2.0');
    }

    return this.http.get<ApiResponse<{ locations: SiteLocation[] }>>(
      `${this.api}/${this.controllerName()}/${siteId}/locations`,
      {
        params,
      },
    );
  }

  public getTrailerTypes(siteId: string): Observable<ApiResponse<{ trailerTypesResponse: SiteTrailerTypes[] }>> {
    return this.http.get<ApiResponse<{ trailerTypesResponse: SiteTrailerTypes[] }>>(
      `${this.api}/${this.controllerName()}/${siteId}/trailer-types`,
    );
  }

  public getTrailerStatuses(siteId: string): Observable<ApiResponse<{ trailerStatusesResponse: SiteTrailerStatus[] }>> {
    return this.http.get<ApiResponse<{ trailerStatusesResponse: SiteTrailerStatus[] }>>(
      `${this.api}/${this.controllerName()}/${siteId}/trailer-statuses`,
    );
  }

  public getScacCodes(siteId: string): Observable<ApiResponse<{ scacs: SiteScacCodes[] }>> {
    return this.http.get<ApiResponse<{ scacs: SiteScacCodes[] }>>(
      `${this.api}/${this.controllerName()}/${siteId}/scacs`,
    );
  }

  public insertScacCodes(
    siteId: string,
    ids: string[],
  ): Observable<
    ApiResponse<{
      assigned: string;
      assignedAt: string;
    }>
  > {
    return this.http.post<
      ApiResponse<{
        assigned: string;
        assignedAt: string;
      }>
    >(`${this.api}/${this.controllerName()}/${siteId}/scacs`, { scacIds: ids });
  }

  public unassignScacCode(siteId: string, scacId: string): Observable<ApiResponse<{ unassigned: string }>> {
    return this.http.delete<ApiResponse<{ unassigned: string }>>(
      `${this.api}/${this.controllerName()}/${siteId}/scacs/${scacId}`,
    );
  }

  public getFreightTypes(
    siteId: string,
    includeDeleted = false,
  ): Observable<ApiResponse<{ freightTypes: SiteFreightTypes[] }>> {
    // If the BE returns the 'deleted' field, we can remove this mess
    if (!includeDeleted) {
      return this.http.get<ApiResponse<{ freightTypes: SiteFreightTypes[] }>>(
        `${this.api}/${this.controllerName()}/${siteId}/freight-types`,
      );
    } else {
      return forkJoin([
        this.http.get<ApiResponse<{ freightTypes: SiteFreightTypes[] }>>(
          `${this.api}/${this.controllerName()}/${siteId}/freight-types`,
        ),
        this.http.get<ApiResponse<{ freightTypes: SiteFreightTypes[] }>>(
          `${this.api}/${this.controllerName()}/${siteId}/freight-types?includeDeleted=${includeDeleted}`,
        ),
      ]).pipe(
        map(([freightResponse, allFreightResponse]) => this.mapFreightTypes(freightResponse, allFreightResponse)),
      );
    }
  }

  public getDockGroups(siteId: string): Observable<DockGroupsResponse> {
    return this.http.get<DockGroupsResponse>(`${this.api}/${this.controllerName()}/${siteId}/dock-groups`);
  }

  public getSiteCapabilities(siteId: string): Result<QueryObserverResult<SiteCapabilities, Error>> {
    return this.query({
      queryKey: [CACHE_KEYS.SITE_CAPABILITY, siteId] as const,
      queryFn: () => {
        return this.http
          .get<
            ApiResponse<{
              siteCapabilityFlag: SiteCapabilities;
            }>
          >(`${this.api}/${this.controllerName()}/${siteId}/capability-flags`)
          .pipe(
            map((x) => {
              if (Array.isArray(x) && x.length === 0) {
                return this.getDefaultCapabilities();
              }
              return x.value.siteCapabilityFlag;
            }),
          );
      },
      staleTime: minutesToMilliseconds(1),
    });
  }

  private mapFreightTypes(
    freightResponse: ApiResponse<{ freightTypes: SiteFreightTypes[] }>,
    allFreightResponse: ApiResponse<{ freightTypes: SiteFreightTypes[] }>,
  ): ApiResponse<{ freightTypes: SiteFreightTypes[] }> {
    const updatedFreightTypes = allFreightResponse.value.freightTypes.map((freightType) => ({
      ...freightType,
      deleted: !freightResponse.value.freightTypes.some((ft) => ft.id === freightType.id),
    }));
    return { ...freightResponse, value: { freightTypes: updatedFreightTypes } };
  }

  private getDefaultCapabilities(): SiteCapabilities {
    return {
      temperatureVarianceAlert: 0,
      fuelVarianceAlert: '',
      liveWelcomeMessage: '',
      liveSpotMessage: '',
      livePullMessage: '',
      allowManualMove: false,
      allowImageCapture: false,
      allowDepartDirectFromDoor: false,
      allowLocationEdit: false,
      isWeightFunctionEnabled: false,
      allowProductProtectionInDoors: false,
      allowProductProtectionForPartialTrailer: false,
      allowFreightTypes: false,
      shipmentDate: false,
      inspection: false,
      storageAttribute: false,
      isTemperatureAndFuelAlertEnabled: false,
      temperatureVariance: 0,
      fuelLevel: '',
      shipmentNumber: true,
      sealNumber: true,
      allowDirectTask: false,
      allowProductProtection: false,
    };
  }
}
